import { ChangeEvent, FormEvent, useCallback, useContext, useEffect, useId, useState } from 'react';
import { endpoints } from '../../../utils/URL';
import axios from 'axios';
import { addToast } from '../../../utils/toastNotifications';
import { IAppState } from '../../../interface/AppInterface';
import { AppContext } from '../../../context/AppContext';
import { IWalletAccountDetails } from '../../../interface/WalletInterface';
import { NairaSymbol } from '../../../utils/mappings';
import { getErrorInfo } from '../../../utils/axiosUtil';

const BulkWithdrawal = () => {
  const id = useId();
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletDetails, setWalletDetails] = useState<IWalletAccountDetails[]>([]);
  const [walletProcessing, setWalletProcessing] = useState(false);
  const [selectedWallets, setSelectedWallets] = useState<IWalletAccountDetails[]>([]);

  const { appState }: { appState: IAppState } = useContext(AppContext);
  const axioInstance = axios.create();

  //get all wallet balances based on client

  const getWalletBalances = useCallback(async () => {
    const clientId = appState.clientId;
    if (clientId === 0) {
      addToast('No client selected', 'error');
      return;
    }

    let url = `${endpoints.wallets.walletAccounts.mainUrl}/client/${clientId}/details`;

    setWalletLoading(true);
    try {
      const response = await axios.get(url);

      setWalletDetails(response.data);
    } catch (error) {
      addToast('Error getting wallet balances', 'error');
    } finally {
      setWalletLoading(false);
    }
  }, [appState.clientId]);

  const selectSingle = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const value = event.target.value;

    //if checked
    if (checked) {
      //if not duplicate
      const wallet = selectedWallets.filter((x) => x.accountId === value.trim());
      if (wallet.length === 0) {
        //https://reactjs.org/docs/hooks-reference.html#functional-updates

        const selectedWallet = walletDetails.filter((x) => x.accountId === value.trim())[0];
        setSelectedWallets((selected) => [...selected, selectedWallet]);
      }
    }
    //if unchecked
    //remove phone number from array
    else {
      const updatedSelections = selectedWallets.filter((x) => x.accountId !== value.trim());
      setSelectedWallets(updatedSelections);
    }
  };

  const selectAll = (event: any) => {
    const checked = event.target.checked;

    //if checked.
    //add all phone numbers to array
    //uncheck all single checkboxes
    const checkboxes = document.getElementsByClassName('single-check');

    if (checked) {
      //uncheck all single checkboxes
      setSelectedWallets([]);

      //check all boxes
      for (let i = 0; i < checkboxes.length; i++) {
        const checkbox = checkboxes[i] as HTMLInputElement;

        //if the checkbox is enabled,check and add it to the selected wallets
        if (!checkbox.disabled) {
          checkbox.checked = true;
          const wallet = walletDetails.filter((x) => x.accountId === checkbox.value.trim())[0];
          setSelectedWallets((selected) => [...selected, wallet]);
        }
      }
    }
    //remove all phone numbers
    //clear all checkboxes
    else {
      setSelectedWallets([]);
      for (let i = 0; i < checkboxes.length; i++) {
        const checkbox = checkboxes[i] as HTMLInputElement;
        checkbox.checked = false;
      }
    }
  };

  const processAllWallets = async (event: FormEvent) => {
    event.preventDefault();
    if (selectedWallets.length === 0) {
      addToast('No wallets selected', 'error');
      return;
    }

    //disable the table
    setWalletProcessing(true);
    // const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    for (let i = 0; i < selectedWallets.length; i++) {
      const wallet = selectedWallets[i];

      updateLoadingStatus(wallet.accountId, 'Processing', 'Processing...');

      const result = await processWalletAsync(wallet);

      updateLoadingStatus(wallet.accountId, result.result, result.resultStatusText);
    }

    setWalletProcessing(false);
    resetCheckboxes();
  };

  const updateLoadingStatus = (accountId: string, status: string, statusText: string) => {
    setWalletDetails((prevDetails) =>
      prevDetails.map((detail) =>
        detail.accountId === accountId
          ? { ...detail, loadingStatus: status, loadingStatusText: statusText }
          : detail
      )
    );
  };

  const processWalletAsync = async (wallet: IWalletAccountDetails) => {
    //process the wallet
    const payload = {
      pharmacyCode: wallet.pharmacyCode,
      amount: wallet.availableBalance,
    };

    const url = `${endpoints.wallets.walletAccounts.mainUrl}/withdrawal`;

    let result = '';
    let resultStatusText = '';

    try {
      await axioInstance.post(url, payload);
      result = 'Success';
      resultStatusText = 'Withdrawal successful';
    } catch (error: any) {
      result = 'Failed';

      if (error && error.response) {
        resultStatusText = getErrorInfo(error);
      }
    }
    return { result, resultStatusText };
  };

  const resetCheckboxes = () => {
    // const checkboxes = document.getElementsByClassName('single-check');
    const allCheck = document.getElementById('all-check') as HTMLInputElement;

    // for (let i = 0; i < checkboxes.length; i++) {
    //   const checkbox = checkboxes[i] as HTMLInputElement;
    //   checkbox.checked = false;
    // }
    allCheck.checked = false;
    setSelectedWallets([]);
  };

  const loadingIcon = (status?: string, statusText?: string) => {
    if (!status) {
      return <span></span>;
    }
    switch (status) {
      case 'Processing':
        return (
          <div>
            <span
              className='spinner-border spinner-border-sm'
              role='status'
              aria-hidden='true'
            ></span>
            <span className='fs-16 ms-1'>{statusText}</span>
          </div>
        );
      case 'Success':
        return (
          <span className='bi bi-check-circle text-success fs-20'>
            <span className='fs-16 ms-1'>{statusText}</span>
          </span>
        );
      case 'Failed':
        return (
          <span className='bi bi-x-circle-fill text-danger fs-20'>
            <span className='fs-16 ms-1'>{statusText}</span>
          </span>
        );
      default:
        console.log('Unknown status', status);
        return <span></span>;
    }
  };

  useEffect(() => {
    getWalletBalances();
    resetCheckboxes();
  }, [getWalletBalances]);

  return (
    <div className='mt-4 mb-5'>
      <div>
        <h5>Bulk Withdrawal</h5>
        <p className='text-muted'>
          Select the wallets you want to withdraw from. You can select all or select individual
          wallets.
        </p>
      </div>
      <div className='mt-4 fw-600 fs-18 d-flex align-items-center justify-content-between'>
        <div>
          Wallets selected: {selectedWallets.length}. Total amount to withdraw:
          <span className='text-success ms-1'>
            {NairaSymbol}
            {selectedWallets.reduce((a, b) => a + b.availableBalance, 0).toLocaleString()}
          </span>
        </div>
        <div className='float-end'>
          <button
            className='btn btn-primary'
            onClick={processAllWallets}
            disabled={walletProcessing}
          >
            Process Withdrawals
          </button>
        </div>
      </div>
      <fieldset className=' table-responsive mt-3' disabled={walletProcessing}>
        <table className='table table-striped table-bordered'>
          <thead>
            <tr>
              <th>
                <label htmlFor='all-check' className='form-check'>
                  Select all{' '}
                  <input
                    type='checkbox'
                    className='form-check-input'
                    id='all-check'
                    name='all-check'
                    onChange={selectAll}
                  />
                </label>
              </th>
              <th>Wallet Account</th>
              <th>Available Balance</th>
              {/* <th scope='col'>Amount</th> */}
              <th> Status</th>
            </tr>
          </thead>
          <tbody>
            {!walletLoading &&
              walletDetails.length > 0 &&
              walletDetails.map((wallet, key) => (
                <tr key={key}>
                  <td>
                    <label htmlFor={`${id}-${key}`} className='form-check'>
                      {wallet.availableBalance < 2000 ? (
                        <span className='text-danger fs-14'>Insufficient Balance</span>
                      ) : (
                        <span className='text-success'>Select wallet</span>
                      )}
                      <input
                        type='checkbox'
                        className='form-check-input single-check'
                        id={`${id}-${key}`}
                        onChange={selectSingle}
                        value={wallet.accountId}
                        disabled={wallet.availableBalance < 2000}
                        checked={selectedWallets.some((x) => x.accountId === wallet.accountId)}
                      />
                    </label>
                  </td>
                  <td>{wallet.pharmacyName}</td>

                  <td className={wallet.availableBalance < 2000 ? 'text-danger' : 'text-success'}>
                    {NairaSymbol}
                    {wallet.availableBalance.toLocaleString()}
                  </td>
                  <td>{loadingIcon(wallet.loadingStatus, wallet.loadingStatusText)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </fieldset>
    </div>
  );
};

export default BulkWithdrawal;
